import { LinkIcon, RefreshCcwIcon, Settings2Icon } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";

import SettingsButton from "@components/settings-button";
import { Button } from "@components/ui/button";
import { toaster } from "@components/ui/toaster";

import { useGetDashboardQuery, useLazyGetReportQuery, useLazySyncBoardQuery } from "@state/apiSlice/requests";
import { selectInit } from "@state/mondaySlice";

const Dashboard = () => {
  const { data, isLoading } = useGetDashboardQuery({});
  const { merchantId } = useSelector(selectInit);
  const navigate = useNavigate();

  const [syncBoard] = useLazySyncBoardQuery();

  const [getReport, { data: reportData, isFetching: isFetchingReport }] = useLazyGetReportQuery();

  if (isLoading) {
    return (
      <Box
        textAlign="center"
        mt={10}
      >
        <Spinner size="xl" />
        <Text mt={4}>Loading...</Text>
      </Box>
    );
  }

  const handleConnectionSettings = () => {
    navigate("/onboarding?reconnectGoogle=true");
  };

  const handleMappingSettings = () => {
    navigate("/mappings?showCloseButton=true");
  };

  const handleSyncBoard = async () => {
    toaster.promise(syncBoard().unwrap(), {
      success: {
        title: "Synced GMC statuses successfully!",
        description: "",
      },
      error: {
        title: "Unable to sync GMC status",
        description: "Please try again",
      },
      loading: { title: "Syncing GMC Status for all board items", description: "Please wait..." },
    });
  };

  const handleOpenLink = (link: string) => {
    if (!link.startsWith("http://") && !link.startsWith("https://")) {
      console.warn("Invalid URL passed to handleOpenLink");
      return;
    }

    const newWindow = window.open(link, "_blank", "noopener,noreferrer");

    if (newWindow) {
      newWindow.focus();
    } else {
      console.error("Popup blocked. Please allow popups for this site.");
    }
  };

  const handleGetReport = async () => {
    try {
      const result = await getReport().unwrap();

      if (result.length === 0)
        toaster.create({
          description: "No issues found to report on",
          type: "success",
        });
    } catch (err) {
      console.error(err);

      toaster.create({
        description: "Error generating report",
        type: "error",
      });
    }
  };

  return (
    <Box
      p={8}
      maxW="1400px"
      mx="auto"
      bg="gray.50"
      minH="100vh"
    >
      {/* Header Section */}
      <Card.Root
        variant="elevated"
        mb={6}
      >
        <CardBody>
          <HStack
            justifyContent="space-between"
            alignItems="center"
          >
            <VStack
              align="start"
              gap={1}
            >
              <Heading
                size="xl"
                color="gray.800"
              >
                GMC Health Dashboard
              </Heading>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                Monitor your Google Merchant Center account health
              </Text>
            </VStack>

            <HStack gap={4}>
              <HStack
                gap={2}
                px={3}
                py={1.5}
                bg="blue.50"
                rounded="md"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <Icon
                  color="blue.500"
                  boxSize={4}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </Icon>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="blue.700"
                >
                  Merchant ID: {merchantId}
                </Text>
              </HStack>

              <HStack gap={2}>
                <SettingsButton
                  disabled={false}
                  menuItems={[
                    {
                      label: "Adjust Google Connection",
                      value: "manualsync",
                      onClick: handleConnectionSettings,
                      disabled: false,
                      icon: <LinkIcon />,
                    },
                    {
                      label: "Mappings",
                      value: "mappings",
                      icon: <Settings2Icon />,
                      disabled: false,
                      onClick: handleMappingSettings,
                    },
                  ]}
                />

                <IconButton
                  variant="solid"
                  aria-label="Sync Board"
                  onClick={handleSyncBoard}
                  colorPalette="blue"
                  size="sm"
                  _hover={{ transform: "rotate(180deg)", transition: "transform 0.3s ease" }}
                >
                  <RefreshCcwIcon />
                </IconButton>
              </HStack>
            </HStack>
          </HStack>
        </CardBody>
      </Card.Root>

      <Grid
        templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
        gap={6}
      >
        {/* Account Issues Section */}
        <GridItem>
          <Card.Root
            variant="elevated"
            h="fit-content"
          >
            <CardHeader pb={3}>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Box
                    p={2}
                    bg="red.100"
                    rounded="lg"
                  >
                    <Icon
                      color="red.600"
                      boxSize={5}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-6h-2v6zm0-8h2V7h-2v2z" />
                      </svg>
                    </Icon>
                  </Box>
                  <Heading
                    size="lg"
                    color="gray.800"
                  >
                    Account Issues
                  </Heading>
                </HStack>
                {data && data.accountIssues.length > 0 && (
                  <Badge
                    colorPalette="red"
                    variant="solid"
                    rounded="full"
                  >
                    {data.accountIssues.length}
                  </Badge>
                )}
              </HStack>
            </CardHeader>

            <CardBody pt={0}>
              {data && data.accountIssues.length > 0 ? (
                <VStack
                  align="stretch"
                  gap={4}
                >
                  {data.accountIssues.map((issue, i) => (
                    <Box
                      key={i}
                      p={4}
                      bg={issue.severity === "CRITICAL" ? "red.50" : "yellow.50"}
                      border="1px solid"
                      borderColor={issue.severity === "CRITICAL" ? "red.200" : "yellow.200"}
                      rounded="lg"
                      _hover={{ shadow: "md", transition: "all 0.2s" }}
                    >
                      <HStack
                        justify="space-between"
                        mb={3}
                      >
                        <Text
                          fontWeight="bold"
                          color="gray.800"
                          flex={1}
                        >
                          {issue.title}
                        </Text>
                        <Badge
                          colorPalette={issue.severity === "CRITICAL" ? "red" : "yellow"}
                          variant="solid"
                          rounded="full"
                          px={3}
                        >
                          {issue.severity}
                        </Badge>
                      </HStack>

                      <Text
                        fontSize="sm"
                        color="gray.700"
                        mb={3}
                        lineHeight="1.6"
                      >
                        {issue.detail}
                      </Text>

                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette={issue.severity === "CRITICAL" ? "red" : "yellow"}
                        onClick={() => handleOpenLink(issue.documentationUri)}
                        _hover={{ bg: issue.severity === "CRITICAL" ? "red.50" : "yellow.50" }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <VStack
                  gap={3}
                  py={8}
                  color="gray.500"
                >
                  <Icon boxSize={12}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Icon>
                  <Text
                    fontSize="lg"
                    fontWeight="medium"
                  >
                    All Good!
                  </Text>
                  <Text
                    fontSize="sm"
                    textAlign="center"
                  >
                    No account issues found.
                  </Text>
                </VStack>
              )}
            </CardBody>
          </Card.Root>
        </GridItem>

        {/* Product Issues Section */}
        <GridItem>
          <Card.Root
            variant="elevated"
            h="fit-content"
          >
            <CardHeader pb={3}>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Box
                    p={2}
                    bg="blue.100"
                    rounded="lg"
                  >
                    <Icon
                      color="blue.600"
                      boxSize={5}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" />
                      </svg>
                    </Icon>
                  </Box>
                  <Heading
                    size="lg"
                    color="gray.800"
                  >
                    Product Statuses
                  </Heading>
                </HStack>
                {data && data.productStatuses.length > 0 && (
                  <Badge
                    colorPalette="blue"
                    variant="solid"
                    rounded="full"
                  >
                    {data.productStatuses.length}
                  </Badge>
                )}
              </HStack>
            </CardHeader>

            <CardBody pt={0}>
              {data && data.productStatuses.length > 0 ? (
                <VStack
                  align="stretch"
                  gap={4}
                >
                  {data.productStatuses.map((status, i) => (
                    <Box
                      key={i}
                      p={4}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      rounded="lg"
                      shadow="sm"
                    >
                      <HStack
                        justify="space-between"
                        mb={4}
                      >
                        <Text
                          fontWeight="bold"
                          color="gray.800"
                        >
                          {status.context} – {status.country}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          fontFamily="mono"
                          bg="gray.100"
                          px={2}
                          py={1}
                          rounded="md"
                        >
                          {Object.entries(status.stats)
                            .map(([key, val]) => `${key}: ${val}`)
                            .join(", ")}
                        </Text>
                      </HStack>

                      <VStack
                        align="stretch"
                        gap={3}
                      >
                        {status.itemLevelIssues.map((issue, j) => (
                          <Box
                            key={j}
                            p={3}
                            bg={issue.severity === "DISAPPROVED" ? "red.50" : "yellow.50"}
                            border="1px solid"
                            borderColor={issue.severity === "DISAPPROVED" ? "red.200" : "yellow.200"}
                            rounded="md"
                          >
                            <HStack
                              justify="space-between"
                              mb={2}
                            >
                              <Text
                                fontWeight="medium"
                                color="gray.800"
                              >
                                {issue.description}
                              </Text>
                              <Badge
                                colorPalette={issue.severity === "DISAPPROVED" ? "red" : "yellow"}
                                variant="solid"
                                rounded="full"
                              >
                                {issue.severity}
                              </Badge>
                            </HStack>

                            <Text
                              fontSize="sm"
                              color="gray.700"
                              mb={3}
                            >
                              {issue.detail}
                            </Text>

                            <Button
                              size="xs"
                              variant="outline"
                              colorPalette={issue.severity === "DISAPPROVED" ? "red" : "yellow"}
                              onClick={() => handleOpenLink(issue.documentationUri)}
                            >
                              Learn More
                            </Button>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <VStack
                  gap={3}
                  py={8}
                  color="gray.500"
                >
                  <Icon boxSize={12}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Icon>
                  <Text
                    fontSize="lg"
                    fontWeight="medium"
                  >
                    All Products Healthy!
                  </Text>
                  <Text
                    fontSize="sm"
                    textAlign="center"
                  >
                    No product issues found.
                  </Text>
                </VStack>
              )}
            </CardBody>
          </Card.Root>
        </GridItem>
      </Grid>

      {/* Generate Report Button */}
      <Card.Root
        variant="elevated"
        mt={6}
      >
        <CardBody>
          <HStack justify="center">
            <Button
              onClick={handleGetReport}
              colorPalette="blue"
              size="lg"
              _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
              transition="all 0.2s"
            >
              Generate Detailed Report
            </Button>
          </HStack>
        </CardBody>
      </Card.Root>

      {/* Product Issue Report */}
      {reportData && reportData.length > 0 && (
        <Card.Root
          variant="elevated"
          mt={6}
        >
          <CardHeader>
            <HStack gap={3}>
              <Box
                p={2}
                bg="purple.100"
                rounded="lg"
              >
                <Icon
                  color="purple.600"
                  boxSize={5}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM7 10H9V12H7V10ZM11 10H17V12H11V10ZM7 14H9V16H7V14ZM11 14H17V16H11V14Z" />
                  </svg>
                </Icon>
              </Box>
              <VStack
                align="start"
                gap={1}
              >
                <Heading
                  size="lg"
                  color="gray.800"
                >
                  Product Issue Report
                </Heading>
                <Text
                  fontSize="sm"
                  color="gray.600"
                >
                  Detailed analysis of {reportData.length} products
                </Text>
              </VStack>
            </HStack>
          </CardHeader>

          <CardBody>
            <VStack
              align="stretch"
              gap={6}
            >
              {reportData.map((row, i) => (
                <Box
                  key={i}
                  p={6}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  rounded="xl"
                  shadow="sm"
                >
                  <HStack
                    justify="space-between"
                    mb={4}
                  >
                    <VStack
                      align="start"
                      gap={1}
                      flex={1}
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="lg"
                        color="gray.800"
                      >
                        {row.productView.title}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        fontFamily="mono"
                      >
                        ID: {row.productView.offerId}
                      </Text>
                    </VStack>

                    <Badge
                      colorPalette={
                        row.productView.aggregatedReportingContextStatus === "NOT_ELIGIBLE_OR_DISAPPROVED"
                          ? "red"
                          : row.productView.aggregatedReportingContextStatus === "ELIGIBLE_LIMITED"
                            ? "yellow"
                            : "green"
                      }
                      variant="solid"
                      rounded="full"
                      px={4}
                      py={1}
                      fontSize="xs"
                    >
                      {row.productView.aggregatedReportingContextStatus.replace(/_/g, " ")}
                    </Badge>
                  </HStack>

                  {row.productView.itemIssues && row.productView.itemIssues.length > 0 ? (
                    <VStack
                      align="stretch"
                      gap={4}
                    >
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        fontWeight="medium"
                      >
                        Issues Found ({row.productView.itemIssues.length}):
                      </Text>

                      {row.productView.itemIssues.map((issue, j) => (
                        <Box
                          key={j}
                          p={4}
                          bg={
                            issue.severity.aggregatedSeverity === "DISAPPROVED"
                              ? "red.50"
                              : issue.severity.aggregatedSeverity === "DEMOTED"
                                ? "yellow.50"
                                : "gray.50"
                          }
                          border="1px solid"
                          borderColor={
                            issue.severity.aggregatedSeverity === "DISAPPROVED"
                              ? "red.200"
                              : issue.severity.aggregatedSeverity === "DEMOTED"
                                ? "yellow.200"
                                : "gray.200"
                          }
                          rounded="lg"
                        >
                          <HStack
                            justify="space-between"
                            mb={3}
                          >
                            <Text
                              fontWeight="medium"
                              color="gray.800"
                              fontFamily="mono"
                              fontSize="sm"
                            >
                              {issue.type.code.replace(/_/g, " ")}
                            </Text>
                            <Badge
                              colorPalette={
                                issue.severity.aggregatedSeverity === "DISAPPROVED"
                                  ? "red"
                                  : issue.severity.aggregatedSeverity === "DEMOTED"
                                    ? "yellow"
                                    : "gray"
                              }
                              variant="solid"
                              rounded="full"
                            >
                              {issue.severity.aggregatedSeverity}
                            </Badge>
                          </HStack>

                          <VStack
                            align="stretch"
                            gap={2}
                          >
                            {issue.type.canonicalAttribute && (
                              <HStack>
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="medium"
                                >
                                  Attribute:
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.800"
                                  fontFamily="mono"
                                  bg="gray.200"
                                  px={2}
                                  py={1}
                                  rounded="md"
                                >
                                  {issue.type.canonicalAttribute}
                                </Text>
                              </HStack>
                            )}

                            {issue.resolution && (
                              <HStack>
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="medium"
                                >
                                  Resolution:
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.800"
                                  bg="blue.100"
                                  px={2}
                                  py={1}
                                  rounded="md"
                                >
                                  {issue.resolution.replace(/_/g, " ")}
                                </Text>
                              </HStack>
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <HStack
                      gap={2}
                      p={3}
                      bg="green.50"
                      border="1px solid"
                      borderColor="green.200"
                      rounded="lg"
                    >
                      <Icon
                        color="green.600"
                        boxSize={4}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Icon>
                      <Text
                        fontSize="sm"
                        color="green.800"
                        fontWeight="medium"
                      >
                        No issues found for this product
                      </Text>
                    </HStack>
                  )}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card.Root>
      )}
    </Box>
  );
};

export default Dashboard;

// return (
//   <Box p={6} maxW="1200px" mx="auto">
//     <HStack justifyContent="flex-end" alignItems="center" alignContent="center" mb={4}>
//       {/* <Heading size="lg">GMC Health</Heading> */}

//       <HStack gap="20px">
//         <Text fontSize="sm">Merchant ID: {merchantId}</Text>

//         <HStack>
//           <SettingsButton
//             disabled={false}
//             menuItems={[
//               {
//                 label: "Adjust Google Connection",
//                 value: "manualsync",
//                 onClick: handleConnectionSettings,
//                 disabled: false,
//                 icon: <LinkIcon />,
//               },
//               {
//                 label: "Mappings",
//                 value: "mappings",
//                 icon: <Settings2Icon />,
//                 disabled: false,
//                 onClick: handleMappingSettings,
//               },
//             ]}
//           />

//           <IconButton variant="solid" aria-label="Sync Board" onClick={handleSyncBoard} colorPalette="blue" size="xs">
//             <RefreshCcwIcon />
//           </IconButton>
//         </HStack>
//       </HStack>
//     </HStack>

//     <VStack align="start" gap="10px">
//       {/* Account Issues */}
//       <Box w="full">
//         <Heading size="md" mb={3}>
//           Account Issues
//         </Heading>
//         {data && data.accountIssues.length > 0 ? (
//           <VStack align="start" mt="20px">
//             {data.accountIssues.map((issue, i) => (
//               <Box key={i} p={4} borderWidth="1px" borderRadius="md" w="full">
//                 <HStack justify="space-between">
//                   <Text fontWeight="bold">{issue.title}</Text>
//                   <Badge colorPalette={issue.severity === "CRITICAL" ? "red" : "yellow"}>{issue.severity}</Badge>
//                 </HStack>
//                 <Text fontSize="sm" mt={2}>
//                   {issue.detail}
//                 </Text>

//                 <Button size="xs" variant="outline" mt={2} onClick={() => handleOpenLink(issue.documentationUri)}>
//                   Learn More
//                 </Button>
//               </Box>
//             ))}
//           </VStack>
//         ) : (
//           <Text color="gray.600">No account issues found.</Text>
//         )}
//       </Box>

//       <Box borderBottom="1px solid #e2e8f0" width="100%" mt="10px" mb="10px" />

//       {/* Product Issues */}
//       <Box w="full">
//         <Heading size="md" mb={3}>
//           Product Statuses
//         </Heading>
//         {data && data.productStatuses.length > 0 ? (
//           data.productStatuses.map((status, i) => (
//             <Box key={i} p={4} borderWidth="1px" borderRadius="md" w="full" mt="10px">
//               <HStack justify="space-between" mb={2}>
//                 <Text fontWeight="bold">
//                   {status.context} – {status.country}
//                 </Text>
//                 <Text fontSize="sm" color="gray.600">
//                   {Object.entries(status.stats)
//                     .map(([key, val]) => `${key}: ${val}`)
//                     .join(", ")}
//                 </Text>
//               </HStack>

//               <SimpleGrid columns={{ base: 1, md: 1 }} mt="20px">
//                 {status.itemLevelIssues.map((issue, j) => (
//                   <Box key={j} p={3} borderWidth="1px" borderRadius="md" mt="10px">
//                     <HStack justify="space-between">
//                       <Text fontWeight="medium">{issue.description}</Text>
//                       <Badge colorPalette={issue.severity === "DISAPPROVED" ? "red" : "yellow"}>{issue.severity}</Badge>
//                     </HStack>
//                     <Text fontSize="sm" mt={1}>
//                       {issue.detail}
//                     </Text>

//                     <Button size="xs" variant="outline" mt={2} onClick={() => handleOpenLink(issue.documentationUri)}>
//                       Learn More
//                     </Button>
//                   </Box>
//                 ))}
//               </SimpleGrid>
//             </Box>
//           ))
//         ) : (
//           <Text color="gray.600">No product issues found.</Text>
//         )}
//       </Box>

//       <Button onClick={handleGetReport}>Get Issue Report</Button>

//       {reportData && reportData.length > 0 && (
//         <Box w="full" mt={10}>
//           <Heading size="md" mb={3}>
//             Product Issue Report
//           </Heading>
//           <VStack align="start" gap={4} w="full">
//             {reportData.map((row, i) => (
//               <Box key={i} p={4} borderWidth="1px" borderRadius="md" w="full">
//                 <HStack justify="space-between" mb={2}>
//                   <Text fontWeight="bold">{row.productView.title}</Text>
//                   <Badge
//                     colorPalette={
//                       row.productView.aggregatedReportingContextStatus === "NOT_ELIGIBLE_OR_DISAPPROVED"
//                         ? "red"
//                         : row.productView.aggregatedReportingContextStatus === "ELIGIBLE_LIMITED"
//                         ? "yellow"
//                         : "green"
//                     }
//                   >
//                     {row.productView.aggregatedReportingContextStatus}
//                   </Badge>
//                 </HStack>

//                 {/* Issues if any */}
//                 {row.productView.itemIssues && row.productView.itemIssues.length > 0 ? (
//                   <VStack align="start" gap={3} mt={3}>
//                     {row.productView.itemIssues.map((issue, j) => (
//                       <Box key={j} p={3} borderWidth="1px" borderRadius="md" w="full">
//                         <HStack justify="space-between" mb={1}>
//                           <Text fontWeight="medium">{issue.type.code}</Text>
//                           <Badge
//                             colorPalette={
//                               issue.severity.aggregatedSeverity === "DISAPPROVED"
//                                 ? "red"
//                                 : issue.severity.aggregatedSeverity === "DEMOTED"
//                                 ? "yellow"
//                                 : "gray"
//                             }
//                           >
//                             {issue.severity.aggregatedSeverity}
//                           </Badge>
//                         </HStack>
//                         {issue.type.canonicalAttribute && (
//                           <Text fontSize="sm" color="gray.600">
//                             Attribute: {issue.type.canonicalAttribute}
//                           </Text>
//                         )}
//                         {issue.resolution && (
//                           <Text fontSize="sm" mt={1}>
//                             Resolution: {issue.resolution}
//                           </Text>
//                         )}
//                       </Box>
//                     ))}
//                   </VStack>
//                 ) : (
//                   <Text fontSize="sm" color="gray.600">
//                     No issues found for this product.
//                   </Text>
//                 )}
//               </Box>
//             ))}
//           </VStack>
//         </Box>
//       )}
//     </VStack>
//   </Box>
// );
